import { watchImmediate } from "@vueuse/core";
import { computed } from "vue";
import { useModifiers } from "./classes";
import { useHost } from "./host";

export const systemColors = [
  "primary",
  "secondary",
  "tertiary",
  "primary-container",
  "secondary-container",
  "success",
  "warning",
  "danger",
  "info",
];

/**
 * Checks if a color is a system color.
 * @param {string} color - The color to check.
 * @returns {boolean} True if the color is a system color, false otherwise.
 *
 * @example
 * isSystemColor("primary") // true
 * isSystemColor("custom-color") // false
 */
export function isSystemColor(color) {
  return systemColors.includes(color);
}

/**
 * Converts a color to a CSS variable.
 * If the color is a system color, it will be converted to the corresponding CSS variable.
 * If the color is not a system color, it will be returned as is.
 * @param {string} color - The color to convert to a CSS variable.
 * @returns {string} The CSS variable.
 *
 * @example
 * toSystemColorVariable("primary") // "var(--sp-sys-color-primary)"
 * toSystemColorVariable("custom-color") // "custom-color"
 */
export function toSystemColorVariable(color) {
  return toColorCssVariable(color);
}

/**
 * Converts a on-color to a CSS variable.
 * @param {string} color - The color to convert to a CSS variable.
 * @returns {string} The CSS variable.
 *
 * @example
 * toSystemOnColorVariable("primary") // "var(--sp-sys-color-on-primary)"
 * toSystemOnColorVariable("custom-color") // "custom-color"
 */
export function toSystemOnColorVariable(color) {
  return toColorCssVariable(color, { onColorVariant: true });
}

/**
 * Converts a on-color-link to a CSS variable.
 * @param {string} color - The color to convert to a CSS variable.
 * @returns {string} The CSS variable.
 *
 * @example
 * toSystemOnColorVariable("primary") // "var(--sp-sys-color-on-primary-link)"
 * toSystemOnColorVariable("custom-color") // "var(--sp-sys-color-link)"
 */
export function toSystemOnColorLinkVariable(color) {
  const fallback = `var(--sp-sys-color-link)`;
  return toColorCssVariable(color, { onColorVariant: true, linkVariant: true, fallback });
}

/**
 * Converts a color to a CSS variable.
 * @param {string} color - The color to convert to a CSS variable.
 * @param {object} options - Optional options.
 * @param {boolean} [options.onColorVariant=false] - If true, the color will be converted to the on-color variant.
 * @param {boolean} [options.linkVariant=false] - If true, the color will be converted to the link variant.
 * @param {string} [options.fallback=color] - The fallback color.
 * @returns {string} The CSS variable.
 *
 * @example
 * toColorCssVariable("primary") // "var(--sp-sys-color-primary)"
 * toColorCssVariable("primary", { onColorVariant: true }) // "var(--sp-sys-color-on-primary)"
 * toColorCssVariable("custom-color") // "custom-color"
 * toColorCssVariable("custom-color", { onColorVariant: true }) // "custom-color"
 * toColorCssVariable("custom-color", { linkVariant: true }) // "var(--sp-sys-color-link)"
 */
function toColorCssVariable(color, options = {}) {
  const { onColorVariant = false, linkVariant = false, fallback = color } = options;

  if (isSystemColor(color)) {
    const prefix = onColorVariant ? "on-" : "";
    const suffix = linkVariant ? "-link" : "";
    const cssProperty = `sp-sys-color-${prefix}${color}${suffix}`;

    return `var(--${cssProperty})`;
  }

  return fallback;
}

/**
 * Applies a color to a CSS property.
 * @param {string} color - The color to apply to the CSS property.
 * @param {HTMLElement} host - The host element.
 * @param {string} cssProperty - The CSS property to apply the color to.
 */
function applyColorProperty(color, host, cssProperty) {
  if (color && host) {
    host.style.setProperty(`--${cssProperty}`, toColorCssVariable(color));
  }
}

/**
 * Sets a color css property on the host element.
 * @param {object} props - The props object. (the props to watch)
 * @param {HTMLElement} root - The root element. (the element to apply the color to)
 * @param {object} options - Optional options.
 * @param {string} options.propName - The prop name to use. (default: "color")
 * @param {string} options.cssProperty - The CSS property to apply the color to. (default: "color")
 *
 * @example
 * const container = ref("id");
 * useColorToCssProperty({ color: "primary" }, container);
 *
 * will set the color css property on the host element to "root.style.color = var(--sp-sys-color-primary)"
 */
export function useColorToCssProperty(props, root, options) {
  const { host } = useHost(root);
  const { propName = "color", cssProperty = "color" } = options ?? {};

  watchImmediate(
    () => [props[propName], host.value],
    ([color, host]) => applyColorProperty(color, host, cssProperty),
    options,
  );
}

/**
 * Applies a color to a CSS property.
 * @param {object} props - The props object.
 * @returns {object} The color modifiers.
 *
 * @example
 * const { colorModifiers } = useColor({ color: "primary" });
 * colorModifiers.value // "--color-primary)"
 */
export function useColor(props) {
  const { modifiers } = useModifiers(props, "color");
  const colorModifiers = computed(() => modifiers.value);

  return {
    colorModifiers,
  };
}
